/**
 * Gate 4 · Slice 4.6 · Voice Guides Panel (advisory context tuning)
 *
 * Compact panel that lists voice guides, lets the operator pin/unpin one,
 * and upload a new inline guide. Sits inside the ImageStudio expanded
 * state as the "advisory context" tuning surface. Provenance
 * (source/version/hash) is preserved server-side on every advisory
 * finding row.
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

export function VoiceGuidesPanel({ groupIndex, brandId }) {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [version, setVersion] = useState("v1");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/creative/voice-guides${brandId ? `?brand_id=${brandId}` : ""}`);
      setGuides(r.data?.voice_guides || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, [brandId]);

  const submitCreate = async () => {
    if (!title.trim() || content.trim().length < 8) {
      toast.error("Title and ≥ 8 char content required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/creative/voice-guides`, {
        title: title.trim(), content: content.trim(), version, brand_id: brandId || null,
      });
      toast.success("Voice guide uploaded");
      setTitle(""); setContent(""); setVersion("v1"); setShowAdd(false);
      await reload();
    } catch (e) {
      toast.error("Upload failed", { description: e?.response?.data?.detail?.reason || e?.message });
    } finally {
      setBusy(false);
    }
  };

  const togglePin = async (g) => {
    try {
      if (g.active) {
        await api.delete(`/creative/voice-guides/${g.id}/pin`);
        toast.success("Voice guide unpinned");
      } else {
        await api.post(`/creative/voice-guides/${g.id}/pin`);
        toast.success("Voice guide pinned");
      }
      await reload();
    } catch (e) {
      toast.error("Pin failed", { description: e?.response?.data?.detail?.reason || e?.message });
    }
  };

  const activeCount = guides.filter((g) => g.active).length;

  return (
    <div
      data-testid={`camp-creative-group-${groupIndex}-voice-guides`}
      style={{
        border: "1px dashed var(--bo-line)",
        borderRadius: 4,
        padding: 6,
        background: "var(--bo-panel-alt)",
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="bo-btn"
        data-testid={`camp-creative-group-${groupIndex}-voice-guides-toggle`}
        style={{
          width: "100%", textAlign: "left",
          background: "transparent", border: "none",
          fontSize: 10.5, fontFamily: "var(--bo-mono)",
          color: "var(--bo-fg)", cursor: "pointer",
          textTransform: "uppercase", letterSpacing: 0.5,
        }}
      >
        {expanded ? "▾" : "▸"} ADVISORY CONTEXT · Voice guides
        <span
          style={{
            marginLeft: 8, fontSize: 10, color: "var(--bo-muted)",
            textTransform: "none", letterSpacing: 0,
          }}
        >
          {activeCount > 0
            ? `${activeCount} pinned · advisory uses pinned voice`
            : `${guides.length} guide${guides.length === 1 ? "" : "s"} · advisory defaults to Brand Brain`}
        </span>
      </button>
      {expanded && (
        <div style={{ marginTop: 6, display: "grid", gap: 6 }}>
          {loading && <div style={{ fontSize: 10, color: "var(--bo-muted)" }}>Loading…</div>}
          {guides.map((g) => (
            <div
              key={g.id}
              data-testid={`camp-creative-group-${groupIndex}-voice-guide-${g.id}`}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "4px 6px",
                border: g.active
                  ? "1px solid var(--bo-accent, #b98a4a)"
                  : "1px solid var(--bo-line)",
                borderRadius: 4,
                background: g.active
                  ? "rgba(185, 138, 74, 0.08)"
                  : "var(--bo-panel)",
              }}
            >
              <span style={{ fontSize: 11.5, fontWeight: 600 }}>{g.title}</span>
              <span
                style={{
                  fontSize: 9, padding: "1px 5px",
                  background: "var(--bo-panel-alt)", borderRadius: 3,
                  fontFamily: "var(--bo-mono)",
                }}
              >
                {g.version}
              </span>
              <span
                style={{
                  fontSize: 9, color: "var(--bo-muted)",
                  fontFamily: "var(--bo-mono)",
                }}
                title={`sha256:${g.content_sha256 || "—"}`}
              >
                {(g.content_sha256 || "").slice(0, 8) || "—"}
              </span>
              {g.active && (
                <span
                  style={{
                    fontSize: 9, padding: "1px 5px",
                    background: "var(--bo-accent, #b98a4a)",
                    color: "#fff", borderRadius: 3,
                    letterSpacing: 0.5,
                  }}
                >
                  PINNED
                </span>
              )}
              <button
                type="button"
                onClick={() => togglePin(g)}
                className="bo-btn"
                data-testid={`camp-creative-group-${groupIndex}-voice-guide-${g.id}-toggle`}
                style={{ marginLeft: "auto", fontSize: 10, padding: "1px 6px" }}
              >
                {g.active ? "Unpin" : "Pin"}
              </button>
            </div>
          ))}
          {!loading && guides.length === 0 && (
            <div style={{ fontSize: 10, color: "var(--bo-muted)" }}>
              No voice guides yet. Advisory falls back to Brand Brain retrieval.
            </div>
          )}
          {showAdd ? (
            <div
              style={{
                marginTop: 4, display: "grid", gap: 4,
                padding: 6, border: "1px dashed var(--bo-line)",
                borderRadius: 4, background: "var(--bo-panel)",
              }}
            >
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Title (e.g., Precipice Voice v2)"
                data-testid={`camp-creative-group-${groupIndex}-voice-guide-new-title`}
                style={{
                  fontSize: 11, padding: "3px 6px",
                  border: "1px solid var(--bo-line)", borderRadius: 3,
                  background: "var(--bo-panel-alt)",
                  color: "var(--bo-fg)",
                }}
              />
              <input
                type="text"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="Version"
                data-testid={`camp-creative-group-${groupIndex}-voice-guide-new-version`}
                maxLength={40}
                style={{
                  fontSize: 11, padding: "3px 6px",
                  border: "1px solid var(--bo-line)", borderRadius: 3,
                  background: "var(--bo-panel-alt)",
                  color: "var(--bo-fg)",
                  width: 100,
                }}
              />
              <textarea
                rows={4}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Paste the approved voice guide content (max 32k chars). Advisory findings will preserve this source/version/hash on every row."
                data-testid={`camp-creative-group-${groupIndex}-voice-guide-new-content`}
                maxLength={32000}
                style={{
                  fontSize: 11, padding: 6,
                  border: "1px solid var(--bo-line)", borderRadius: 3,
                  background: "var(--bo-panel-alt)",
                  color: "var(--bo-fg)",
                  fontFamily: "inherit",
                }}
              />
              <div style={{ display: "flex", gap: 4 }}>
                <button
                  type="button"
                  onClick={() => setShowAdd(false)}
                  className="bo-btn"
                  style={{ fontSize: 10, padding: "2px 8px" }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitCreate}
                  disabled={busy}
                  className="bo-btn bo-btn-primary"
                  data-testid={`camp-creative-group-${groupIndex}-voice-guide-new-submit`}
                  style={{ fontSize: 10, padding: "2px 10px", marginLeft: "auto" }}
                >
                  {busy ? "…" : "Save"}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="bo-btn"
              data-testid={`camp-creative-group-${groupIndex}-voice-guide-add`}
              style={{
                fontSize: 10, padding: "3px 8px",
                width: "fit-content",
              }}
            >
              + Add guide
            </button>
          )}
        </div>
      )}
    </div>
  );
}
